/* eslint-disable max-lines */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router";
import {reverse} from "../../routing";
import {useLazyQuery, useQuery} from "@apollo/client";
import {useModal} from "../../components/ModalProvider";
import {useCurrentUser} from "../../App.context";
import query, {
  challengeQuery,
  useRecalculateScoreMutation,
  useReanalyseSessionCompetenceMutation as useReanalyseCompetenceResultsMutation,
  userQuery,
} from "./results.graphql";
import {getSessionResultFeedbackQuery, SessionResultFeedbackData} from "../../redux/player/graphql";

import ReactDocumentTitle from "../../components/ReactDocumentTitle";
import Layout, {Header, Main} from "../../components/Layout";
import Container from "../../ui/container";
import Breadcrumbs from "../../ui/breadcrumbs";
import ReactLink from "../../components/ReactLink";
import {ParametricResultHelp} from "../../components/ParametricResultHelp";
import {
  CompetenceAnalysisState,
  DisplayParametricResultMode,
  EducationSessionRecordState,
  EducationSessionResult,
  Challenge,
  ChallengeMode,
  User,
} from "../../schema";
import {isUserAdmin, Scenario} from "../../types";
import SessionActionsList from "../../components/results/SessionActionsList";
import {AlertCircleIcon, CheckmarkIcon, CrossIcon, PlayIcon} from "../../ui/icons";
import Spinner from "../../ui/spinner";
import WithTooltip from "../../ui/tooltip";
import FullscreenVideo from "../../ui/fullscreenvideo";
import RecordLink from "../../components/RecordLink";
import Link from "../../ui/link";

import {formatDateTime, formatDuration} from "../../time";
import {PlayableAction} from "../../redux/player/types";
import {getParametricScore} from "../../utils";

import classes from "./results.module.css";
import cn from "classnames";
import ResultsDropdown from "../../components/results/ResultsDropdown";

export default function ResultsScene() {
  const {t} = useTranslation();
  const currentUser = useCurrentUser();
  const isDeveloper = !!currentUser?.isDeveloper;

  const [videoSrc, setVideoSrc] = useState<string>();

  const resetVideoSrc = useCallback(() => setVideoSrc(undefined), []);

  const onPlayButtonClick = useCallback((e: React.MouseEvent) => {
    const src = e.currentTarget.getAttribute("data-id");

    setVideoSrc(src ?? undefined);
  }, []);

  const {challengeId} = useParams<{challengeId: Challenge["id"]}>();
  const {userId} = useParams<{userId: User["id"]}>();

  const {data: challengeData} = useQuery<{
    challenge: Challenge;
    actions: PlayableAction[];
    additionalScenarios: Scenario[];
  }>(challengeQuery, {
    variables: {
      challengeId,
    },
  });

  const {data} = useQuery<{results: EducationSessionResult[]}>(query, {
    variables: {
      challengeId,
      userId,
    },
    pollInterval: 2000,
  });

  const [user, setUser] = useState<Pick<User, "email" | "fullName"> | null>(null);

  const [loadUser, {data: userData}] = useLazyQuery<{user: User}>(userQuery);

  useEffect(() => {
    if (userId) {
      loadUser({
        variables: {userId},
      });
    }
  }, [userId, loadUser]);

  useEffect(() => {
    if (userId) {
      if (userData?.user) {
        setUser(userData.user);
      }
    } else {
      if (currentUser) {
        setUser(currentUser);
      }
    }
  }, [userId, userData, currentUser]);

  const {challenge, actions, additionalScenarios} = challengeData ?? {};
  const results = data?.results;

  const {refetch: refetchFeedback} = useQuery<SessionResultFeedbackData>(getSessionResultFeedbackQuery, {
    skip: true,
  });

  const hasScore = useMemo(() => challenge?.mode !== ChallengeMode.COMPETENCE_TEST, [challenge]);
  const {add: addModal} = useModal();
  const [recalculateScoreMutation] = useRecalculateScoreMutation();
  const [reanalyseCompetenceResultsMutation] = useReanalyseCompetenceResultsMutation();

  const recalculateScore = useCallback(
    (e: React.MouseEvent) => {
      const sessionId = e.currentTarget.getAttribute("data-id");
      e.preventDefault();
      recalculateScoreMutation({
        variables: {
          sessionId: sessionId!,
        },
      });
    },
    [recalculateScoreMutation],
  );

  const isReanalyseAccessed = useCallback(
    (competenceAnalysisState: CompetenceAnalysisState) =>
      !!currentUser &&
      isUserAdmin(currentUser) &&
      !!challenge &&
      (challenge.mode === ChallengeMode.COMPETENCE_TEST || challenge.mode === ChallengeMode.RPG_MODE) &&
      (competenceAnalysisState === CompetenceAnalysisState.ERROR ||
        competenceAnalysisState === CompetenceAnalysisState.UNKNOWN),
    [challenge, currentUser],
  );
  const reanalyseCompetenceResults = useCallback(
    (sessionId: string) => () => {
      reanalyseCompetenceResultsMutation({
        variables: {
          sessionId: sessionId,
        },
      });
    },
    [reanalyseCompetenceResultsMutation],
  );

  const isInProgressWarningVisible = useCallback(
    (sessionResult: EducationSessionResult) =>
      sessionResult.isContentMatches &&
      (challenge?.mode === ChallengeMode.COMPETENCE_TEST || challenge?.mode === ChallengeMode.RPG_MODE) &&
      sessionResult.competenceAnalysisState === CompetenceAnalysisState.IN_PROGRESS,
    [challenge],
  );

  const isErrorWarningVisible = useCallback(
    (sessionResult: EducationSessionResult) =>
      sessionResult.isContentMatches &&
      (challenge?.mode === ChallengeMode.COMPETENCE_TEST || challenge?.mode === ChallengeMode.RPG_MODE) &&
      (sessionResult.competenceAnalysisState === CompetenceAnalysisState.ERROR ||
        sessionResult.competenceAnalysisState === CompetenceAnalysisState.UNKNOWN),
    [challenge],
  );

  const showDetailedResults = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();

      const index = e.currentTarget.getAttribute("data-id");
      if (!index || !results) {
        return;
      }
      const result = results[parseInt(index)];

      if (!challenge || !actions || !challenge.showDetailedResults) {
        return;
      }

      const feedbackResult = await refetchFeedback({
        sessionId: result.sessionId,
      });

      const generativeFeedbackText = feedbackResult.data?.result?.resultFeedback?.text;

      addModal({
        className: cn(classes.modal, classes.textSelectable),
        size: "xl",
        header: <span>{t("player.results.detailed")}</span>,
        content: (
          <div className={classes.actionsListContainer}>
            {challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST && (
              <div className={classes.customParams}>
                <b className={classes.customParameter}>
                  {t("player.results.customParameters")}
                  <ParametricResultHelp displayParametricResultMode={challenge.displayParametricResultMode} />
                </b>
                {result.parametricResults.map((pr) => (
                  <span className={classes.customParameter}>
                    {pr.parameterName}: {getParametricScore(pr, challenge.displayParametricResultMode)}
                    {challenge.displayParametricResultMode === DisplayParametricResultMode.PERCENTAGE && "%"}
                  </span>
                ))}
              </div>
            )}

            {challenge.mode === ChallengeMode.COMPETENCE_TEST && result.competenceResults && (
              <div data-testid="competence-results" className={classes.competenceResults}>
                <b className={classes.competenceResult}>{t("player.results.competenceResults")}:</b>
                {result.competenceResults.map((competenceResult) => (
                  <span className={classes.competenceResult}>
                    {competenceResult.competence}: {Math.floor(competenceResult.score * 100)}%
                  </span>
                ))}
              </div>
            )}

            {generativeFeedbackText && (
              <>
                <div className={classes.subHelpText}>{t("components.ResultsDisplay.generativeFeedback.title")}:</div>
                <div className={classes.feedbackText}>{generativeFeedbackText}</div>
              </>
            )}

            <SessionActionsList
              challenge={challenge}
              actionsResults={result.actionResults}
              initAvatarAction={result.initAvatarAction}
              actions={actions}
              additionalScenarios={additionalScenarios}
              finishedScenarios={result.finishedScenariosIds}
            />
          </div>
        ),
      });
    },
    [challenge, results, actions, additionalScenarios, addModal, t, refetchFeedback],
  );

  return (
    <>
      <ReactDocumentTitle title={t("results.title")} />

      <Layout>
        <Header />
        <Main>
          <Container className={classes.main}>
            <Breadcrumbs className={classes.breadcrumbs}>
              <ReactLink href={reverse("player")}>{t("breadcrumbs.playerIndex")}</ReactLink>
              <span>{challenge?.name ?? t("breadcrumbs.challenge")}</span>
              <span>{t("breadcrumbs.results")}</span>
            </Breadcrumbs>
            <h1 className={classes.h1}>{t("results.results")}</h1>
            <table className={classes.tableRoot} cellPadding={0} cellSpacing={0}>
              <thead>
                <tr>
                  {hasScore && <td className={classes.value}>{t("results.result")}</td>}
                  <td className={classes.value}>{t("results.time")}</td>
                  <td className={classes.value}>{t("results.hints")}</td>
                  <td className={classes.value}>{t("results.detailed")}</td>
                  <td className={classes.value}>{t("results.startedAt")}</td>
                  <td className={cn(classes.value, classes.centerAlign)}>{t("results.record")}</td>
                  <td className={cn(classes.value, classes.centerAlign)}></td>
                </tr>
              </thead>
              <tbody>
                {results &&
                  results.map((item, index) => (
                    <tr key={item.sessionId} data-testid={item.sessionId}>
                      {hasScore && (
                        <td className={classes.value}>
                          <div className={classes.valueInner}>
                            {item.passed ? (
                              <CheckmarkIcon className={cn(classes.icon, classes.passed)} />
                            ) : (
                              <CrossIcon className={cn(classes.icon, classes.failed)} />
                            )}

                            <span className={classes.score}>{item.score}</span>
                          </div>
                        </td>
                      )}
                      <td className={classes.value}>{formatDuration(item.time)}</td>
                      <td className={classes.value}>{item.usedHints}</td>
                      <td className={cn(classes.value, classes.extraWide)}>
                        <div className={classes.detailedCell}>
                          {item.isContentMatches && challenge?.showDetailedResults ? (
                            <Link data-id={index} disabled={!item.isContentMatches} onClick={showDetailedResults}>
                              {t("results.showDetailed")}
                            </Link>
                          ) : (
                            <span className={classes.noDetails}>
                              {!challenge?.showDetailedResults
                                ? t("results.noDetailedResult")
                                : t("results.challengeChanged")}
                            </span>
                          )}
                          {isInProgressWarningVisible(item) && (
                            <WithTooltip
                              as="span"
                              className={classes.competenceAnalysisIndicator}
                              helpText={t("results.competenceAnalysisInProgressTooltip")}
                            >
                              <Spinner
                                data-testid="competence-in-progress-spinner"
                                size="small"
                                className={classes.competenceAnalysisSpinner}
                              />
                            </WithTooltip>
                          )}
                          {isErrorWarningVisible(item) && (
                            <WithTooltip
                              as="span"
                              className={classes.competenceAnalysisIndicator}
                              helpText={t("results.competenceAnalysisErrorTooltip")}
                            >
                              <AlertCircleIcon
                                data-testid="competence-error-icon"
                                className={classes.competenceAnalysisErrorIcon}
                              />
                            </WithTooltip>
                          )}
                        </div>
                      </td>
                      <td className={classes.value}>{formatDateTime(item.startedAt)}</td>
                      <td className={cn(classes.value, classes.centerAlign)}>
                        <RecordLink
                          className={classes.recordButton}
                          as={Link}
                          data-id={item.record}
                          disabled={item.recordState !== EducationSessionRecordState.READY}
                          onClick={onPlayButtonClick}
                          state={item.recordState}
                          textVariant="short"
                        >
                          {item.recordState === EducationSessionRecordState.READY && (
                            <PlayIcon className={classes.playIcon} />
                          )}
                        </RecordLink>
                      </td>
                      <td className={cn(classes.value, classes.centerAlign)}>
                        {challenge && actions && additionalScenarios && (
                          <ResultsDropdown
                            reportSaveProps={{
                              user,
                              challenge,
                              actions,
                              additionalScenarios,
                              results: item,
                            }}
                            reanalyseCompetenceResultsProps={{
                              onReanalyseStart: reanalyseCompetenceResults(item.sessionId),
                              isVisible: isReanalyseAccessed(item.competenceAnalysisState),
                            }}
                          />
                        )}
                      </td>
                      {isDeveloper && challenge?.mode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES && (
                        <td className={classes.value}>
                          <Link data-id={item.sessionId} onClick={recalculateScore}>
                            {"Recalculate"}
                          </Link>
                        </td>
                      )}
                    </tr>
                  ))}
                {!results?.length && (
                  <tr>
                    <td colSpan={10}>{t("results.empty")}</td>
                  </tr>
                )}
              </tbody>
            </table>
            {videoSrc && <FullscreenVideo onClose={resetVideoSrc} src={videoSrc} />}
          </Container>
        </Main>
      </Layout>
    </>
  );
}
